import { useCallback } from 'react';

import WebLinkTargets from '../../../../constants/WeblinkTargets';
import {
  ExternalMediaMedialink,
  ExternalMediaMedialinkWeblink,
  isExternalMediaWeblink,
  isExternalMediaGeoGebra,
  isExternalMediaH5P,
  isExternalMediaYoutube,
} from '../../_api/MedialinksApi';
import { Node } from '../../_api/TableOfContentsApi';
import GeoGebraModal from '../../_modals/GeoGebraModal';
import WeblinkModal from '../../_modals/WeblinkModal';
import H5PModal from '../../_modals/H5PModal';
import EmbeddableMediaModal from '../../_modals/EmbeddableMediaModal';
import MedialinkContentType from '../../_components/common/MedialinkContentType';
import parseYoutubeURL from '../../../../utils/parseYoutubeURL';

function openWeblink(medialink: ExternalMediaMedialinkWeblink, node: Node) {
  const url = medialink.externalMedia.href;

  switch (medialink.externalMedia.target) {
    case WebLinkTargets.MODAL:
      const { host } = new URL(url);

      if (host.includes('h5p.org') || host.includes('h5p.com')) {
        return (
          <H5PModal
            id="h5p-modal"
            medialink={medialink}
            node={node}
            url={url}
            width={medialink.externalMedia.width}
            height={medialink.externalMedia.height}
          />
        );
      }

      const parsedYoutubeURL = parseYoutubeURL(url);

      if (parsedYoutubeURL) {
        return (
          <EmbeddableMediaModal
            node={node}
            url={parsedYoutubeURL}
            name={medialink.name}
            id="embeddable-media-modal"
            contentType={<MedialinkContentType medialink={medialink} />}
          />
        );
      }

      return (
        <WeblinkModal
          url={url}
          node={node}
          id="weblink-modal"
          medialink={medialink}
          weblinkInfo={medialink.externalMedia}
        />
      );

    case WebLinkTargets.SAME_WINDOW:
      window.location.assign(url);
      break;

    case WebLinkTargets.NEW_WINDOW: //handled by anchor tag
  }
}

export default function useExternalMediaMedialink() {
  const openExternalMediaMedialink = useCallback((medialink: ExternalMediaMedialink, node: Node) => {
    if (isExternalMediaWeblink(medialink)) {
      return openWeblink(medialink, node);
    }

    if (isExternalMediaGeoGebra(medialink)) {
      return <GeoGebraModal id="geo-gebra-modal" medialink={medialink} node={node} />;
    }

    if (isExternalMediaH5P(medialink)) {
      return (
        <H5PModal
          id="h5p-modal"
          medialink={medialink}
          node={node}
          url={`https://h5p.org/h5p/embed/${medialink.externalMedia.embedId}`}
          width={medialink.externalMedia.width}
          height={medialink.externalMedia.height}
        />
      );
    }

    if (isExternalMediaYoutube(medialink)) {
      const query = new URLSearchParams({
        autoplay: '1',
        ...(medialink.externalMedia.start && { start: medialink.externalMedia.start.toString() }),
        ...(medialink.externalMedia.end && { end: medialink.externalMedia.end.toString() }),
      });

      const url = `https://www.youtube.com/embed/${medialink.externalMedia.embedId}?${query}`;

      return (
        <EmbeddableMediaModal
          node={node}
          url={url}
          name={medialink.name}
          id="embeddable-media-modal"
          contentType={<MedialinkContentType medialink={medialink} />}
        />
      );
    }
  }, []);

  return { openExternalMediaMedialink };
}
